import { uniq } from "lodash";
import reduceReducers from "reduce-reducers";

import { WEIGH_LOT_SCAN } from "constants/actionTypes";
import { WeighLotScanStatus } from "constants/weighScanning";

import {
  apiModelOfflineFetchReducer,
  apiModelOfflineUpdateReducer,
  apiModelOfflineCreateReducer,
  apiModelOfflineDeleteReducer,
  apiModelOfflineCommentReducer,
  bulkUpdateCases,
} from "lib/reducers";
import { deserializeWeighLotScan } from "lib/serializers/weighLotScans";

const create = apiModelOfflineCreateReducer(WEIGH_LOT_SCAN, {
  deserializer: deserializeWeighLotScan,
});

const deleteReducer = apiModelOfflineDeleteReducer(WEIGH_LOT_SCAN);

const fetch = apiModelOfflineFetchReducer(WEIGH_LOT_SCAN, {
  deserializer: deserializeWeighLotScan,
});

const update = apiModelOfflineUpdateReducer(WEIGH_LOT_SCAN, {
  deserializer: deserializeWeighLotScan,
});

const comment = apiModelOfflineCommentReducer(WEIGH_LOT_SCAN);

function bulkUpdateAndCreateReducer(state, action) {
  switch (action.type) {
    case WEIGH_LOT_SCAN.BULK_UPDATE_OR_CREATE.REQUEST: {
      return bulkUpdateCases(state, action, "byId", deserializeWeighLotScan);
    }
    // The backend runs the whole keep/ignore decision (siblings ignored, reweigh vs mapped
    // status) and returns every Weigh Lot Scan it touched - merge that in directly instead of
    // waiting on a separate changes-since fetch, the same way a plain UPDATE.SUCCESS would.
    case WEIGH_LOT_SCAN.KEEP.SUCCESS:
    case WEIGH_LOT_SCAN.IGNORE.SUCCESS: {
      const touchedWeighLotScans = action.payload.weigh_lot_scans;
      const merged = bulkUpdateCases(
        state,
        { ...action, payload: touchedWeighLotScans },
        "byId",
        deserializeWeighLotScan,
      );

      // bulkUpdateCases spreads the previous byId entry (which may still carry a
      // `_previousStatus` snapshot from `applyOptimisticKeep`/`.Ignore`) before the deserialized
      // response, and the response never has a `_previousStatus` key to overwrite it with - so a
      // stale snapshot would otherwise survive the commit and corrupt a later rollback.
      const byId = { ...merged.byId };
      for (const { id } of touchedWeighLotScans) {
        if (byId[id] && "_previousStatus" in byId[id]) {
          const { _previousStatus, ...rest } = byId[id];
          byId[id] = rest;
        }
      }

      return { ...merged, byId };
    }
    default:
      return state;
  }
}

/**
 * Every non-deduplicated Weigh Lot Scan sharing `weighLotScan`'s EID, including itself - i.e. the
 * set the backend's keep decision considers together (see `KeepWeighLotScans._keep`). Everything
 * this needs (eid, weighLotId, status) already lives in this slice's own `byId`.
 */
function findKeepFamily(byId, weighLotScan) {
  return Object.values(byId).filter(
    other =>
      other.eid === weighLotScan.eid &&
      other.status !== WeighLotScanStatus.DEDUPLICATED,
  );
}

function isReweigh(family) {
  return uniq(family.map(member => member.weighLotId)).length > 1;
}

function snapshotAndSetStatus(byId, id, status) {
  const weighLotScan = byId[id];
  byId[id] = {
    ...weighLotScan,
    status,
    syncing: true,
    _previousStatus: weighLotScan._previousStatus ?? weighLotScan.status,
  };
}

/**
 * Optimistically predicts a keep decision: the resulting status (MAPPED vs REWEIGHED) depends
 * only on how many distinct Weigh Lots this EID spans, and marks every sibling sharing the EID
 * as IGNORED, the same as the backend will - all computable from this slice's own `byId`, so
 * there's nothing the backend knows here that the client doesn't already have.
 *
 * Siblings already `syncing` (claimed by another keep/ignore still in flight) are left alone
 * rather than re-decided, so two overlapping requests touching the same reweigh pair don't flip
 * each other's predictions back and forth before either resolves - whichever actually lands last
 * wins once SUCCESS/FAILURE settle it, same as it would server-side.
 */
function applyOptimisticKeep(state, weighLotScanIds) {
  const byId = { ...state.byId };
  for (const id of weighLotScanIds) {
    const weighLotScan = byId[id];
    if (!weighLotScan || weighLotScan.syncing) {
      continue;
    }

    const family = findKeepFamily(byId, weighLotScan);
    const keptStatus = isReweigh(family)
      ? WeighLotScanStatus.REWEIGHED
      : WeighLotScanStatus.MAPPED;

    snapshotAndSetStatus(byId, id, keptStatus);

    for (const sibling of family) {
      if (sibling.id !== id && !sibling.syncing) {
        snapshotAndSetStatus(byId, sibling.id, WeighLotScanStatus.IGNORED);
      }
    }
  }
  return { ...state, byId };
}

/**
 * Reverts everything `applyOptimisticKeep` could have touched for these ids - re-deriving the
 * same family (eids don't change, so this still finds the same members) rather than needing the
 * touched-ids list to have survived until FAILURE.
 */
function rollbackOptimisticKeep(state, weighLotScanIds) {
  const byId = { ...state.byId };
  const touchedIds = new Set();

  for (const id of weighLotScanIds) {
    const weighLotScan = byId[id];
    if (!weighLotScan) {
      continue;
    }
    touchedIds.add(id);
    for (const sibling of findKeepFamily(byId, weighLotScan)) {
      touchedIds.add(sibling.id);
    }
  }

  for (const id of touchedIds) {
    const weighLotScan = byId[id];
    if (weighLotScan && "_previousStatus" in weighLotScan) {
      const { _previousStatus, ...rest } = weighLotScan;
      byId[id] = { ...rest, status: _previousStatus, syncing: false };
    }
  }
  return { ...state, byId };
}

/**
 * Ignoring is much simpler than keeping - it only ever affects the exact requested ids, always
 * resolves to IGNORED, and doesn't touch any siblings - so it just needs a plain snapshot/restore.
 */
function applyOptimisticIgnore(state, weighLotScanIds) {
  const byId = { ...state.byId };
  for (const id of weighLotScanIds) {
    if (byId[id] && !byId[id].syncing) {
      snapshotAndSetStatus(byId, id, WeighLotScanStatus.IGNORED);
    }
  }
  return { ...state, byId };
}

function rollbackOptimisticIgnore(state, weighLotScanIds) {
  const byId = { ...state.byId };
  for (const id of weighLotScanIds) {
    const weighLotScan = byId[id];
    if (weighLotScan && "_previousStatus" in weighLotScan) {
      const { _previousStatus, ...rest } = weighLotScan;
      byId[id] = { ...rest, status: _previousStatus, syncing: false };
    }
  }
  return { ...state, byId };
}

function keepIgnoreRequestReducer(state, action) {
  switch (action.type) {
    case WEIGH_LOT_SCAN.KEEP.REQUEST:
      return applyOptimisticKeep(state, action.payload);
    case WEIGH_LOT_SCAN.IGNORE.REQUEST:
      return applyOptimisticIgnore(state, action.payload);
    case WEIGH_LOT_SCAN.KEEP.FAILURE:
      return rollbackOptimisticKeep(state, action.meta.weighLotScanIds);
    case WEIGH_LOT_SCAN.IGNORE.FAILURE:
      return rollbackOptimisticIgnore(state, action.meta.weighLotScanIds);
    default:
      return state;
  }
}

const weighLotScanReducer = reduceReducers(
  create,
  comment,
  deleteReducer,
  fetch,
  update,
  bulkUpdateAndCreateReducer,
  keepIgnoreRequestReducer,
);

export default weighLotScanReducer;
